import Button from '@risk-smart/themed-cloudscape-components/button';
import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Table from '@risksmart-app/components/src/table';
import type { GetOversightResultsByParentIdQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import DeleteModal from 'src/components/delete-modal';
import TabHeader from 'src/components/tab-header';
import type { ObjectWithContributors } from 'src/rbac/Permission';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import { useDeleteOversightResults } from '@/hooks/mutations/oversight-result/useDeleteOversightResults';
import { useDeleteResultNotification } from '@/hooks/useMutationResultNotification';

import { useGetCollectionStatelessTableProps } from './config';
import { getOversightResultTableFields } from './oversightRatingRegisterUtils';
import type { OversightResultRegisterFields } from './types';

interface Props {
  loading: boolean;
  assessmentId: string;
  records: GetOversightResultsByParentIdQuery | undefined;
  parent: ObjectWithContributors;
}

const OversightRatingRegister: FC<Props> = ({
  loading,
  records,
  assessmentId,
  parent,
}) => {
  const { t } = useTranslation(['common'], { keyPrefix: 'assessmentResults' });
  const tableProps = useGetCollectionStatelessTableProps(
    getOversightResultTableFields(records, assessmentId)
  );
  const [selectedOversightResults, setSelectedOversightResults] = useState<
    OversightResultRegisterFields[]
  >([]);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);

  const {
    hasPermission: canDeleteDocumentOversightResult,
    loading: canDeleteDocumentOversightResultLoading,
  } = useHasPermissionQuery('delete:document_oversight_result', parent);
  const {
    hasPermission: canDeleteObligationOversightResult,
    loading: canDeleteObligationOversightResultLoading,
  } = useHasPermissionQuery('delete:obligation_oversight_result', parent);
  const {
    hasPermission: canDeleteRiskControlledOversightResult,
    loading: canDeleteRiskControlledOversightResultLoading,
  } = useHasPermissionQuery('delete:risk_controlled_oversight_result', parent);
  const {
    hasPermission: canDeleteRiskUncontrolledOversightResult,
    loading: canDeleteRiskUncontrolledOversightResultLoading,
  } = useHasPermissionQuery(
    'delete:risk_uncontrolled_oversight_result',
    parent
  );
  const {
    hasPermission: canDeleteControlTestOversightResult,
    loading: canDeleteControlTestOversightResultLoading,
  } = useHasPermissionQuery('delete:control_test_oversight_result', parent);

  const isLoading =
    canDeleteDocumentOversightResultLoading ||
    canDeleteObligationOversightResultLoading ||
    canDeleteRiskControlledOversightResultLoading ||
    canDeleteRiskUncontrolledOversightResultLoading ||
    canDeleteControlTestOversightResultLoading;

  const canDeleteOversightResult =
    !isLoading &&
    (canDeleteDocumentOversightResult ||
      canDeleteObligationOversightResult ||
      canDeleteRiskControlledOversightResult ||
      canDeleteRiskUncontrolledOversightResult ||
      canDeleteControlTestOversightResult);

  const { deleteOversightResults, loading: deleteLoading } =
    useDeleteOversightResults();

  const onDelete = useDeleteResultNotification({
    entityName: t('entity_name'),
    asyncAction: async () => {
      await deleteOversightResults(
        selectedOversightResults?.map((s) => s.Id) ?? []
      );
      setSelectedOversightResults([]);
      setIsDeleteModalVisible(false);

      return true;
    },
    failureAction: () => {
      setIsDeleteModalVisible(false);
    },
  });

  return (
    <>
      <Table
        {...tableProps}
        loading={loading}
        selectionType={canDeleteOversightResult ? 'multi' : undefined}
        selectedItems={selectedOversightResults}
        trackBy={'Id'}
        onSelectionChange={({ detail }) => {
          setSelectedOversightResults(detail.selectedItems);
        }}
        header={
          <SpaceBetween size={'m'}>
            <TabHeader
              actions={
                <SpaceBetween direction={'horizontal'} size={'xs'}>
                  {canDeleteOversightResult && (
                    <Button
                      formAction={'none'}
                      variant={'normal'}
                      disabled={!selectedOversightResults.length}
                      onClick={() => setIsDeleteModalVisible(true)}
                    >
                      {t('delete_button')}
                    </Button>
                  )}
                </SpaceBetween>
              }
            ></TabHeader>
          </SpaceBetween>
        }
        resizableColumns={true}
        variant={'embedded'}
        columnDefinitions={tableProps.columnDefinitions}
        items={tableProps.items}
        loadingText={t('loading_message')}
        sortingDisabled={false}
      />
      <DeleteModal
        isVisible={isDeleteModalVisible}
        loading={deleteLoading}
        header={t('delete_button')}
        onDelete={onDelete}
        onDismiss={() => setIsDeleteModalVisible(false)}
      >
        {t('confirm_delete_message')}
      </DeleteModal>
    </>
  );
};

export default OversightRatingRegister;
